import React from 'react'
import PropTypes from 'prop-types'
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    Button,
    Stack,
    Text,
    HStack,
    Flex,
    Box,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    CareShieldIcon,
    DoubleChevronRightIcon,
    FreeDeliveryIcon,
    WarrantyReturnIcon,
} from '../icons'
import Close16Icon from '../icons/shared/action/Close16Icon'

const WarrantyCareModal = ({isOpen, onClose, exploreLink}) => {
    const styles = useMultiStyleConfig('WarrantyCareModal')
    const modalStyles = useMultiStyleConfig('Modal', {variant: 'warrantyCare'})
    const intl = useIntl()
    const discoverLink = intl.formatMessage({
        id: 'product_view.care.modal.discover_link.link',
        defaultMessage: '',
    })
    const tncLink = intl.formatMessage({
        id: 'product_view.care.modal.explore_link.link',
        defaultMessage: '',
    })

    return (
        <Modal isOpen={isOpen} onClose={onClose} blockScrollOnMount={false} isCentered>
            <ModalOverlay {...modalStyles.modalOverlay} />
            <ModalContent
                {...modalStyles.modalContent}
                {...styles.container}
                aria-labelledby="warranty-care-modal-title"
                aria-describedby="warranty-care-modal-description"
            >
                <ModalHeader {...styles.header}>
                    <Flex {...styles.headerFlex}>
                        <Box {...styles.shieldIcon}>
                            <CareShieldIcon {...styles.shieldIconSvg} />
                        </Box>
                        <Box {...styles.closeButtonWrapper}>
                            <Close16Icon
                                {...styles.closeButtonIcon}
                                type="button"
                                onClick={onClose}
                                aria-label="Close warranty modal"
                                tabIndex={0}
                            />
                        </Box>
                    </Flex>
                    <Flex {...styles.headerContent}>
                        {/* Title */}
                        <Text id="warranty-care-modal-title" {...styles.title}>
                            <FormattedMessage
                                id="product_view.care.modal.title"
                                defaultMessage="De'Longhi Care"
                            />
                        </Text>

                        {/* Description */}
                        <Text id="warranty-care-modal-description" {...styles.description}>
                            <FormattedMessage
                                id="product_view.care.modal.description"
                                defaultMessage="Comprehensive protection and support for your De'Longhi product."
                            />
                        </Text>
                    </Flex>
                </ModalHeader>

                {/* Modal Body */}
                <ModalBody {...styles.body}>
                    <Stack {...styles.contentStack}>
                        {/* Topics */}
                        <Stack as="ul" {...styles.topicsStack}>
                            <WarrantyTopicRowProtection />
                            <WarrantyTopicRowSupport />
                            <WarrantyTopicRowPeace />
                        </Stack>
                        {(discoverLink || tncLink || exploreLink) && (
                            <Box>
                                {discoverLink && (
                                    <Box {...styles.exploreLinkWrapper}>
                                        <Button
                                            as="a"
                                            href={discoverLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant={'link-button'}
                                            {...styles.exploreLink}
                                        >
                                            <Text as="span">
                                                <FormattedMessage
                                                    id="product_view.care.modal.discover_link"
                                                    defaultMessage="Explore De'Longhi Care"
                                                />
                                            </Text>
                                            <DoubleChevronRightIcon {...styles.exploreLinkIcon} />
                                        </Button>
                                    </Box>
                                )}
                                {(tncLink || exploreLink) && (
                                    <Box {...styles.exploreLinkWrapper}>
                                        <Button
                                            as="a"
                                            href={tncLink || exploreLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            variant={'link-button'}
                                            {...styles.exploreLink}
                                        >
                                            <Text as="span">
                                                <FormattedMessage
                                                    id="product_view.care.modal.explore_link"
                                                    defaultMessage="Read Terms & Conditions"
                                                />
                                            </Text>
                                            <DoubleChevronRightIcon {...styles.exploreLinkIcon} />
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Stack>
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}

const WarrantyTopicRowProtection = () => {
    const styles = useMultiStyleConfig('WarrantyCareModal')

    return (
        <HStack as="li" {...styles.topicRow}>
            <Box {...styles.topicIcon}>
                <FreeDeliveryIcon {...styles.topicIconSvg} />
            </Box>
            <Stack {...styles.topicContent}>
                <Text {...styles.topicTitle}>
                    <FormattedMessage
                        id="product_view.care.modal.topic.protection.title"
                        defaultMessage="Extended protection"
                    />
                </Text>
                <Text {...styles.topicDescription}>
                    <FormattedMessage
                        id="product_view.care.modal.topic.protection.description"
                        defaultMessage="Add extra years of coverage for accidental breakdowns and repairs."
                    />
                </Text>
            </Stack>
        </HStack>
    )
}

const WarrantyTopicRowSupport = () => {
    const styles = useMultiStyleConfig('WarrantyCareModal')

    return (
        <HStack as="li" {...styles.topicRow}>
            <Box {...styles.topicIcon}>
                <CareShieldIcon {...styles.topicIconSvg} />
            </Box>
            <Stack {...styles.topicContent}>
                <Text {...styles.topicTitle}>
                    <FormattedMessage
                        id="product_view.care.modal.topic.support.title"
                        defaultMessage="Dedicated support"
                    />
                </Text>
                <Text {...styles.topicDescription}>
                    <FormattedMessage
                        id="product_view.care.modal.topic.support.description"
                        defaultMessage="Get fast help from trained specialists for diagnosis and service booking."
                    />
                </Text>
            </Stack>
        </HStack>
    )
}

const WarrantyTopicRowPeace = () => {
    const styles = useMultiStyleConfig('WarrantyCareModal')

    return (
        <HStack as="li" {...styles.topicRow}>
            <Box {...styles.topicIcon}>
                <WarrantyReturnIcon {...styles.topicIconSvg} />
            </Box>
            <Stack {...styles.topicContent}>
                <Text {...styles.topicTitle}>
                    <FormattedMessage
                        id="product_view.care.modal.topic.peace.title"
                        defaultMessage="Peace of mind"
                    />
                </Text>
                <Text {...styles.topicDescription}>
                    <FormattedMessage
                        id="product_view.care.modal.topic.peace.description"
                        defaultMessage="Enjoy your machine every day knowing your investment is protected."
                    />
                </Text>
            </Stack>
        </HStack>
    )
}

WarrantyCareModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    exploreLink: PropTypes.string,
}

export default WarrantyCareModal
